import { format, subDays } from 'date-fns';

import { type Assets } from '@air/api';
import { ADDON_DISABLED, BAD_REQUEST, matchesAirror, NO_PERMISSION, NOT_FOUND } from '@air/errors';
import { type Succeeded } from '@air/server-sdk';

export type InternalMetricsSummaryRequest = NonNullable<Parameters<typeof Assets.Metrics.summary>[0]>;
export type InternalMetricsSummaryResponse = Succeeded<typeof Assets.Metrics.summary>;
export type MetricsWindow = NonNullable<InternalMetricsSummaryRequest['window']>;
export type InternalMetricValues = InternalMetricsSummaryResponse['totals'];

const INSIGHTS_WINDOW_DAYS = 30;
const COMPACT_METRIC_THRESHOLD = 1_000;
const INTERNAL_METRICS_SUMMARY_RETRY_LIMIT = 3;

export const toDateOnly = (date: Date) => format(date, 'yyyy-MM-dd');

export const getDefaultInsightsWindow = (today = new Date()): MetricsWindow => ({
  start: toDateOnly(subDays(today, INSIGHTS_WINDOW_DAYS - 1)),
  end: toDateOnly(today),
});

export const getInternalMetricValue = (values: InternalMetricValues | undefined, field: 'views' | 'downloads') =>
  values?.[`internal.${field}`] ?? 0;

const COMPACT_METRIC_FORMATTER = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumSignificantDigits: 2,
});

/** Formats metrics to compact notation. e.g. 1000 → 1k, 1,000,000 → 1m */
export const formatCompactMetricValue = (value: number) =>
  value < COMPACT_METRIC_THRESHOLD ? String(value) : COMPACT_METRIC_FORMATTER.format(value).toLowerCase();

/** Retry transient metrics failures while leaving terminal API responses available for manual retry. */
export const shouldRetryInternalMetricsSummary = (failureCount: number, error: Error) => {
  if (
    matchesAirror(error, NO_PERMISSION) ||
    matchesAirror(error, NOT_FOUND) ||
    matchesAirror(error, BAD_REQUEST) ||
    matchesAirror(error, ADDON_DISABLED)
  ) {
    return false;
  }

  return failureCount < INTERNAL_METRICS_SUMMARY_RETRY_LIMIT;
};
